import { nextTick, reactive, ref } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import TempWorkerContractApiTempWorker from '@/api/TempWorkerContractApiTempWorker';
import { isEmpty } from 'lodash-es';
import AvailabilityApi from '@/api/AvailabilityApiTempWorker';
import UnavailabilityApiTempWorker from '@/api/UnavailabilityApiTempWorker';
import SpecificAvailabilityApiTempworker from '@/api/SpecificAvailabilityApiTempWorker';
import { useAside } from '@orion.ui/orion';
import UserProfileSpecificAvailabilitiesAsideNew from '@/components/user-profile/UserProfileSpecificAvailabilitiesAsideNew.vue';
import UserProfileUnavailabilityAsideNew from '@/components/user-profile/UserProfileUnavailabilityAsideNew.vue';

import TempWorkerAvailabilityEntity from '@/entity/TempWorkerAvailabilityEntity';

type Props = SetupProps<typeof DashboardTempWorkerCalendarSetupService.props>

export default class DashboardTempWorkerCalendarSetupService extends BaseSetupService<Props> {
	static readonly props = {};

	readonly _calendar = ref<OrionDateTable>();

	private readonly state = reactive({
		dataLoaded: false,
		selectedDay: new Date(),
		periodSelected: undefined as Undef<ISpecificAvailabilityModel>,
		unavailabilities: [] as IUnavailabilityModel[],
		availabilities: undefined as Undef<IAvailabilityModel>,
		specificAvailabilities: [] as ISpecificAvailabilityModel[],
		missions: [] as IUpcomingTempWorkerContractModel[],
		currentMonth: new Date().getMonth(),
		currentYear: new Date().getFullYear(),
	});

	get dataLoaded () { return this.state.dataLoaded; }
	get periodSelected () { return this.state.periodSelected; }
	get selectedDay () { return this.state.selectedDay; }
	set selectedDay (val) { this.state.selectedDay = val; }

	get isCurrentOrUpcoming () {
		return new Date().getWeekNumber() - 1 < this.selectedDay.getWeekNumber() || this.selectedDay.getFullYear() > new Date().getFullYear();
	}

	get minDate () {
		return new Date(this.state.currentYear, this.state.currentMonth, 1);
	}

	get canGoPrevMonth () {
		if (this.state.dataLoaded && this._calendar.value) {
			return (
				(this._calendar.value.getCurrentMonth() > this.state.currentMonth)
			|| (this._calendar.value.getCurrentYear() > this.state.currentYear)
			);
		}
	}

	get isAvailable () {
		return this.isSpecificDay === undefined && !this.isMissionDay && !this.isUnavailableDay;
	}

	get isSpecificDay () {
		let result = undefined;
		const currentDayName = this.state.selectedDay.toLocaleDateString('en-EN', { weekday: 'long' }).toLowerCase();
		this.state.specificAvailabilities.find((period) => {
			if (this.state.selectedDay >= new Date(period.from) && (this.state.selectedDay <= new Date(period.to))) {
				this.state.periodSelected = period;
				result = period[currentDayName as keyof typeof period];
			}
		});
		return result;
	}

	get isUnavailableDay () {
		let result = undefined;
		this.state.unavailabilities.find((period) => {
			if (this.state.selectedDay >= new Date(period.startDate) && (this.state.selectedDay <= new Date(period.endDate)))
				result = period;
		});
		return result;
	}

	get isMissionDay () {
		let result = undefined;
		this.state.missions.find((period) => {
			if (this.state.selectedDay >= new Date(period.startDate) && (this.state.selectedDay <= new Date(period.endDate)))
				result = period;
		});
		return result;
	}

	get periods () {
		return this.formatPeriods();
	}

	constructor (props?: Props) {
		super(props);
	}

	protected async onBeforeMountAsync () {
		this.state.dataLoaded = false;
		await this.getPeriodsAsync();
		this.state.dataLoaded = true;
	}

	protected onMounted () {
		this.registerBusEvent('unvavailabilityDeleted', (id) => {
			this.state.unavailabilities.deleteWhere('id', id);
		});
		this.registerBusEvent('specificAvailabilityDeleted', (id) => {
			this.state.specificAvailabilities.deleteWhere('id', id);
		});
		this.registerBusEvent('specificAvailabilityAdded', (specific) => {
			this.state.specificAvailabilities.push(specific);
		});
		this.registerBusEvent('specificAvailabilityUpdated', (specific) => {
			this.state.specificAvailabilities.map((x) => {
				if (x.id === specific.id)
					Object.assign(x, specific);
			});
		});
	}

	async getPeriodsAsync () {
		const { data: missionData } = await TempWorkerContractApiTempWorker.getUpcomingMissions(new Date().toPost());
		if (!isEmpty(missionData)) {
			this.state.missions = missionData;
		}

		const { data: availabilitiesData } = await AvailabilityApi.get();
		if (!isEmpty(availabilitiesData)) {
			this.state.availabilities = availabilitiesData;
		}

		const { data: unavailabilitiesData } = await UnavailabilityApiTempWorker.list();
		if (!isEmpty(unavailabilitiesData)) {
			this.state.unavailabilities = unavailabilitiesData;
		}

		const { data: specificAvailabilitiesData } = await SpecificAvailabilityApiTempworker.list();
		if (!isEmpty(specificAvailabilitiesData)) {
			this.state.specificAvailabilities = specificAvailabilitiesData;
		}
	}

	formatPeriods () {
		const periods = [] as Orion.Period[];
		if (this.state.missions) {
			this.state.missions?.forEach((mission) => {
				const period = { ...mission } as unknown as Orion.Period;
				period.start = new Date(mission.startDate);
				period.end = new Date(mission.endDate);
				period.color = 'brand';
				period.label = 'Mission';
				periods.push(period);
			});
		}

		if (this.state.unavailabilities.length) {
			this.state.unavailabilities?.forEach((unavailabilitiy) => {
				const period = { ...unavailabilitiy } as unknown as Orion.Period;
				period.start = new Date(unavailabilitiy.startDate);
				period.end = new Date(unavailabilitiy.endDate);
				period.color = 'warning';
				period.label = 'Indisponibilité';
				periods.push(period);
			});
		}

		if (this.state.specificAvailabilities.length) {
			this.state.specificAvailabilities?.forEach((specific) => {
				const period = { ...specific } as unknown as Orion.Period;
				period.start = new Date(specific.from);
				period.end = new Date(specific.to);
				period.color = 'info';
				period.label = 'Disponibilité particulière';
				periods.push(period);
			});
		}
		return periods;
	}

	verifyAndOpenAsideSpecAvailPeriod () {
		let specificAvailabilityId = undefined;
		this.state.specificAvailabilities?.forEach((period, index) => {
			if ((new Date(period.from).toMidnight().valueOf() <= this.selectedDay.toMidnight().valueOf())
				&& (new Date(period.to).toMidnight().valueOf() >= this.selectedDay.toMidnight().valueOf())) {
				specificAvailabilityId = index;
				return specificAvailabilityId;
			}
		});
		this.openAddSpecificAvailabilitiesAside();
	}

	openAddSpecificAvailabilitiesAside () {
		useAside({
			Nested: UserProfileSpecificAvailabilitiesAsideNew,
			NestedProps: {
				availabilityEntity: new TempWorkerAvailabilityEntity(),
				day: this.selectedDay,
			},
			events: {
				add: (Aside, payload: ISpecificAvailabilityModel) => {
					nextTick(() => {
						this.state.specificAvailabilities.push(payload);
						Aside.close();
					});
				},
			},
		});
	}

	showUnavailabilityAsideNew () {
		useAside({
			Nested: UserProfileUnavailabilityAsideNew,
			events: {
				add: (Aside, payload: IUnavailabilityModel) => {
					nextTick(() => {
						this.state.unavailabilities.push(payload);
						Aside.close();
					});
				},
			},
		});
	}
}
